import React from 'react';
import { Skull } from 'lucide-react';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { User } from '../../../types';

interface TerminationRecordCardProps {
  user: User;
}

function contactLine(
  contacts:
    | {
        email?: string | null;
        phone?: string | null;
        username?: string | null;
        telegram_user_id?: string | null;
        telegram_username?: string | null;
      }
    | null
    | undefined
) {
  if (!contacts) return [];
  return [
    contacts.email ? `Email ${contacts.email}` : null,
    contacts.phone ? `Phone ${contacts.phone}` : null,
    contacts.username ? `Username ${contacts.username}` : null,
    contacts.telegram_user_id ? `Telegram ${contacts.telegram_user_id}` : null,
    contacts.telegram_username && contacts.telegram_username !== contacts.telegram_user_id
      ? `@${contacts.telegram_username}`
      : null,
  ].filter(Boolean) as string[];
}

const TerminationRecordCard: React.FC<TerminationRecordCardProps> = ({ user }) => {
  const fmt = useFormatDate();
  if (user.status !== 'terminated') return null;

  const log = user.closeReleaseLog;
  const reason = log?.reason || user.terminate_reason;
  const closedAt = log?.createdAt || user.terminated_at;
  const closedBy = log?.closedByName
    ? log.closedByName
    : user.terminated_by
      ? 'Admin'
      : 'Shop';
  const originalLines = contactLine(log?.originalContacts);

  return (
    <Card className="border-stone-200 bg-stone-50/80">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-stone-800">
          <Skull className="h-5 w-5 shrink-0" aria-hidden />
          Close record
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-stone-700">
          This account is permanently closed. The player cannot log in or perform wallet or game transactions.
        </p>
        {reason ? (
          <div>
            <span className="text-muted-foreground">Reason: </span>
            <span className="font-medium text-foreground">{reason}</span>
          </div>
        ) : null}
        {closedAt ? (
          <div>
            <span className="text-muted-foreground">Closed at: </span>
            <span className="font-medium">{fmt(closedAt)}</span>
          </div>
        ) : null}
        <div>
          <span className="text-muted-foreground">Closed by: </span>
          <span className="font-medium">{closedBy}</span>
        </div>
        {originalLines.length > 0 ? (
          <div>
            <span className="text-muted-foreground">Original contacts: </span>
            <span className="font-medium">{originalLines.join(' · ')}</span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default TerminationRecordCard;
